/**
 * Words that name an item's role, measured on prod names (98.7% precise on uploaded file names). Each
 * reader composes its own families: a file name gets strict + file-name words; a sample name gets only
 * the strict NC/negative words, since there "control" is a control site, EB/FB are site codes and "water"
 * is a sample material; a type-column value gets strict + role words, as the column already says it is
 * about roles.
 */

export type ItemRole = 'SAMPLE' | 'NEGATIVE_CONTROL'

export const STRICT_CONTROL_FAMILIES: readonly RegExp[] = [
  /^(nc|ntc|nk|ncp|nce)\d*$/,
  /^(pcr)?neg(ativ|ative)?(e)?(kontrolle|control|ctrl)?\d*$/
]

export const FILE_NAME_CONTROL_FAMILIES: readonly RegExp[] = [
  /^(blank|blk|leer)\d*$/,
  /^(eb|fb)\d*$/,
  /^(control|ctrl|kontrolle)\d*$/,
  /^(h2o|water|wasser)\d*$/
]

export const TYPE_VALUE_CONTROL_FAMILIES: readonly RegExp[] = [
  /^(blank|blk|leer|leerwert)\d*$/,
  /^(control|controls|ctrl|kontrolle|kontrollen)\d*$/
]

/** A positive control is not a negative one, whatever else the name says. */
export const POSITIVE_CONTROL = /^(pos|positiv|positive|poskontrolle|mock|zymo)/

const SAMPLE_WORDS = new Set([
  'sample',
  'samples',
  'probe',
  'proben',
  'umweltprobe',
  'feldprobe',
  'field',
  'environmental',
  'env',
  'regular',
  'normal',
  'unknown',
  'biological'
])

const TRUTHY = new Set(['x', 'y', 'yes', 'ja', 'j', '1', 'true', 'wahr', '✓', '✔', '*'])
const FALSY = new Set(['n', 'no', 'nein', '0', 'false', 'falsch', '-'])

export const tokensOf = (value: string): string[] => {
  const split = (text: string) =>
    text
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(/[^A-Za-z0-9]+/)
      .filter(Boolean)
      .map((token) => token.toLowerCase())
  /* a second reading catches compounds such as `PCRneg` whose case change is upper→lower */
  return [...split(value), ...split(value.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'))]
}

export type ValueReading =
  { kind: 'role'; role: ItemRole } | { kind: 'flag'; set: boolean } | { kind: 'positive-control' } | { kind: 'empty' } | { kind: 'unknown' }

/** What one cell of a type column says, before the column as a whole decides how flags and empties read. */
export const readTypeValue = (raw: string): ValueReading => {
  const value = raw.trim()
  if (value === '') {
    return { kind: 'empty' }
  }
  const lower = value.toLowerCase()
  if (TRUTHY.has(lower)) {
    return { kind: 'flag', set: true }
  }
  if (FALSY.has(lower)) {
    return { kind: 'flag', set: false }
  }
  const tokens = tokensOf(value)
  if (tokens.some((token) => POSITIVE_CONTROL.test(token))) {
    return { kind: 'positive-control' }
  }
  if (tokens.some((token) => [...STRICT_CONTROL_FAMILIES, ...TYPE_VALUE_CONTROL_FAMILIES].some((family) => family.test(token)))) {
    return { kind: 'role', role: 'NEGATIVE_CONTROL' }
  }
  if (tokens.length > 0 && tokens.every((token) => SAMPLE_WORDS.has(token))) {
    return { kind: 'role', role: 'SAMPLE' }
  }
  return { kind: 'unknown' }
}

/** Header text reduced for comparison: case, separators and a trailing parenthetical such as "(x)" dropped. */
export const normalizeHeader = (header: string): string =>
  header
    .replace(/\([^)]*\)\s*$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]/g, '')
